import {
  createContext,
  FC,
  PropsWithChildren,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { DefaultValues } from 'react-hook-form'
import { useParams } from 'react-router'

import { getDefaultPersistentState } from './config'
import { PersistentState } from './type'
import { getDefaultPageState } from '../config'
import { useExternalStore } from '../externalStore'
import { State } from '../types'
import { StepPage } from 'directFlow/types'
import { FlowContext, useFlowContext } from 'directFlow/utils/FlowContext'
import Loader from 'components/Loader'

export type PersistentStateContextValue = {
  onPageInit: (currentPage: StepPage) => void
  onPageClose: VoidFunction
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateField: (currentPage: StepPage, fieldName: string, value: any) => void
  removeField: (currentPage: StepPage, fieldName: string) => void
  updatePageFields: <CurrentPage extends StepPage>(
    currentPage: CurrentPage,
    state: DefaultValues<State[CurrentPage]>,
  ) => void
  isLoading: boolean
  getPageState: <CurrentPage extends StepPage>(
    page: CurrentPage,
  ) => DefaultValues<State[typeof page]>
  updateFormProgress: (page: StepPage) => void
  stateRef: RefObject<PersistentState>
  /**
   * Store information that needs to be persisted across the direct flow,
   * but aren't related to a specific page
   */
  clear: VoidFunction
  updateState: (state: PersistentState) => void
}

const PersistentStateContext = createContext<PersistentStateContextValue | null>(null)

export const PersistentStateManager: FC<PropsWithChildren> = ({ children }) => {
  const { quoteId } = useParams<{ quoteId: string }>()
  const flowContext = useFlowContext()
  const [isLoading, setLoading] = useState(true)

  if (!quoteId) {
    throw new Error('PersistentStateManager used on a route without a quote ID')
  }

  // `validStateRef` is only used when the data is loaded, after the `isLoading` statement
  // So putting a non-null assertion here is safe
  const validStateRef = useRef<PersistentState>(null!)

  const externalStore = useExternalStore({
    quoteId,
    type: flowContext === FlowContext.DIRECT ? 'directFlow' : 'postQuoteEdit',
  })

  const isUpdateEnabledRef = useRef(true)

  useEffect(() => {
    if (externalStore.data === undefined) {
      return
    }

    validStateRef.current = externalStore.data
    setLoading(false)
  }, [externalStore.data])

  if (isLoading) {
    return <Loader />
  }

  const handlePageInit: PersistentStateContextValue['onPageInit'] = (currentPage) => {
    isUpdateEnabledRef.current = true

    if (validStateRef.current.pageState[currentPage] === undefined) {
      validStateRef.current.pageState[currentPage] = getDefaultPageState(currentPage)
      externalStore.sync(validStateRef.current)
    }
  }

  const handlePageClose: PersistentStateContextValue['onPageClose'] = () => {
    // Update on persistent state must be disabled before page transition
    // (like going back to a previous page or going to the next one)
    // since we navigate to a new page and this would unmount current page fields
    // Without this, it would remove field's values from the state
    isUpdateEnabledRef.current = false
  }

  const handleUpdateField: PersistentStateContextValue['updateField'] = (
    currentPage,
    fieldName,
    value,
  ) => {
    if (!isUpdateEnabledRef.current) {
      return
    }

    // @ts-expect-error
    validStateRef.current.pageState[currentPage][fieldName] = value
    externalStore.sync(validStateRef.current)
  }

  const handleRemoveField: PersistentStateContextValue['removeField'] = (
    currentPage,
    fieldName,
  ) => {
    if (!isUpdateEnabledRef.current) {
      return
    }

    // @ts-expect-error
    delete validStateRef.current.pageState[currentPage][fieldName]
    externalStore.sync(validStateRef.current)
  }

  const handleUpdatePageFields: PersistentStateContextValue['updatePageFields'] = (page, state) => {
    validStateRef.current.pageState[page] = state
    externalStore.sync(validStateRef.current)
  }

  const getPageState: PersistentStateContextValue['getPageState'] = (page) => {
    const pageState = validStateRef.current.pageState[page]
    const defaultValues = getDefaultPageState(page)

    if (pageState) {
      const pageFieldKeys = Object.keys(defaultValues)

      return pageFieldKeys.reduce<DefaultValues<State[typeof page]>>((acc, fieldKey) => {
        // @ts-expect-error
        if (pageState[fieldKey] !== undefined) {
          // @ts-expect-error
          acc[fieldKey] = pageState[fieldKey]
        }

        return acc
      }, defaultValues)
    }

    return defaultValues
  }

  const updateFormProgress = (page: StepPage) => {
    if (!validStateRef.current.formProgress.includes(page)) {
      validStateRef.current.formProgress.push(page)
      externalStore.sync(validStateRef.current)
    }
  }

  const clear = () => {
    validStateRef.current = getDefaultPersistentState()
    externalStore.clear()
  }

  const updateState = (state: PersistentState) => {
    validStateRef.current = state
    externalStore.sync(state)
  }

  return (
    <PersistentStateContext.Provider
      value={{
        onPageInit: handlePageInit,
        onPageClose: handlePageClose,
        updateField: handleUpdateField,
        removeField: handleRemoveField,
        updatePageFields: handleUpdatePageFields,
        updateFormProgress,
        getPageState,
        stateRef: validStateRef,
        isLoading: externalStore.isLoading,
        clear,
        updateState,
      }}
    >
      {children}
    </PersistentStateContext.Provider>
  )
}

export const usePersistentState = (): PersistentStateContextValue => {
  const contextValue = useContext(PersistentStateContext)

  if (contextValue === null) {
    throw new Error('`usePersistentState` should have `PersistentStateManager` as a parent')
  }

  return contextValue
}
